/*******************************************************************************
 * ALMA - Atacama Large Millimeter Array Copyright (c) UKATC - UK Astronomy
 * Technology Centre, Science and Technology Facilities Council, 2011 (in the
 * framework of the ALMA collaboration). All rights reserved.
 *
 * This library is free software you can redistribute it and/or modify it under
 * the terms of the GNU Lesser General Public License as published by the Free
 * Software Foundation either version 2.1 of the License, or (at your option)
 * any later version.
 *
 * This library is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the GNU Lesser General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this library if not, write to the Free Software Foundation, Inc.,
 * 59 Temple Place, Suite 330, Boston, MA 02111-1307 USA
 ******************************************************************************/

const MILLISECS_PER_SEC = 1000
const SECONDS_PER_MINUTE = 60
const MINUTES_PER_DEGREE = 60
const DEC_MAX_DEGREES = 90
const MILLISECS_PER_MINUTE = SECONDS_PER_MINUTE * MILLISECS_PER_SEC
const FW_BEFORE = 2
const AS_A_DECIMAL = 10

export const MILLISECS_PER_UNIT = 60 * MILLISECS_PER_MINUTE

export function checkBoundsDDMMSS (sign, degrees, minutes, seconds, milliSecs) {
  const isValidDeclination = (
    (!isNaN(degrees) && degrees < DEC_MAX_DEGREES) &&
    (
      (
        (!isNaN(minutes) && minutes <= MINUTES_PER_DEGREE) &&
        (!isNaN(seconds) && seconds <= SECONDS_PER_MINUTE) &&
        (!isNaN(milliSecs) && milliSecs <= MILLISECS_PER_SEC)
      ) ||
      (
        degrees === DEC_MAX_DEGREES &&
        (minutes === 0 && seconds === 0 && milliSecs === 0)
      )
    )
  )

  return isValidDeclination
}

export function stringToMilliarcsecNoColons (sourceString) {
  const paddedLen = 6
  const zeros = '000000'
  /** return value */
  var result = {
    isValid: true,
    value: 0
  }
  /** What sign to use */
  var sign = 1
  /** integer substring */
  var main = null
  /** fractional substring */
  var frac = '000'

  sourceString = sourceString.trim()

  // handle neg sign then the rest should follow
  if (sourceString.charAt(0) === '-') {
    sign = -1
    sourceString = sourceString.substring(1)
  }

  // Isolate 'integer' and 'fractional'
  // parts of value string:
  // +123456.789 --> 123456 and 789
  // ----------------------------------------
  sourceString = sourceString.trim()
  main = '000000'

  const stringParts = sourceString.split('.')

  if (stringParts.length > 0) {
    if (stringParts[0].length < 2) {
      stringParts[0] = '0' + stringParts[0]
    }
    main = ('' + stringParts[0] + zeros).substring(0, paddedLen)

    if (stringParts.length > 1) {
      const l = stringParts[0].length
      main = ('' + zeros + stringParts[0] + zeros).substring(l, paddedLen + l)
      frac = (stringParts[1] + zeros).substring(0, 3)
    }
  }

  frac = frac.substring(0, 3)
  const uu = main.substring(0 * FW_BEFORE, 1 * FW_BEFORE)
  const mm = main.substring(1 * FW_BEFORE, 2 * FW_BEFORE)
  const ss = main.substring(2 * FW_BEFORE, 3 * FW_BEFORE)

  const units = parseInt(uu, AS_A_DECIMAL)
  const minutes = parseInt(mm, AS_A_DECIMAL)
  const seconds = parseInt(ss, AS_A_DECIMAL)
  const milliSecs = parseInt(frac, AS_A_DECIMAL)
  if (checkBoundsDDMMSS(sign, units, minutes, seconds, milliSecs)) {
    result.isValid = true
    result.value = sign * (units * MILLISECS_PER_UNIT + minutes * MILLISECS_PER_MINUTE + seconds * MILLISECS_PER_SEC + milliSecs)
  } else {
    result.isValid = false
    result.value = 0
  }
  return result
}

/**
 * Called as part of the construction of a new declination - will calculate the
 * new value in degrees and milliarcseconds and update them in the declination object
 * If there are any validation errors.
 *
 */
export function regenerateDeclinationValues (declination) {
  const returnedDeclination = Object.assign({}, declination)

  const inpString = returnedDeclination.formattedValue
  // shortcut if no declination value has been supplied
  if (inpString.length === 0) {
    returnedDeclination.mas = 0
    returnedDeclination.inDegrees = 0
    returnedDeclination.validationError = 'Declination is required'
    returnedDeclination.isValid = false
    return returnedDeclination
  }

  var sourceString = inpString.replace(/(^\s+|\s+$)/g, '')
  // Drop useless leading '+' sign
  if (sourceString.startsWith('+')) {
    sourceString = sourceString.substring(1)
  }
  // Let someone else deal with the no-colons case
  if (sourceString.indexOf(':') === -1) {
    const result = (stringToMilliarcsecNoColons(sourceString))
    returnedDeclination.isValid = result.isValid
    if (result.isValid) {
      returnedDeclination.mas = result.value
      returnedDeclination.inDegrees = (result.value / MILLISECS_PER_UNIT)
    } else {
      returnedDeclination.validationError = 'Invalid number : ' + declination.formattedValue
      returnedDeclination.mas = 0
      returnedDeclination.inDegrees = 0
    }
    return (returnedDeclination)
  }

  const st = sourceString.split(':')
  var units
  var unitString = ''
  if (st.length > 0) {
    unitString = st[0]
  }
  var minutes = 0
  var seconds = 0
  var milliSecs = 0
  var sign = +1
  if (st.length > 1 && st[1].length > 0) {
    minutes = parseInt(st[1], AS_A_DECIMAL)
    if (st.length > 2 && st[2].length > 0) {
      const secondParts = st[2].split('.')
      if (secondParts.length > 0) {
        seconds = parseInt(secondParts[0], AS_A_DECIMAL)
        if (secondParts.length > 1) {
          const msStr = secondParts[1].padEnd(3,'0')
          milliSecs = parseInt(msStr, AS_A_DECIMAL)
        }
      }
    }
  }
  if (unitString.startsWith('-')) {
    sign = -1
    units = -parseInt(unitString, AS_A_DECIMAL)
  } else {
    units = parseInt(unitString, AS_A_DECIMAL)
  }
  if (checkBoundsDDMMSS(sign, units, minutes, seconds, milliSecs)) {
    const mas = sign * (units * MILLISECS_PER_UNIT + minutes * MILLISECS_PER_MINUTE +
      seconds * MILLISECS_PER_SEC + milliSecs)
    returnedDeclination.mas = mas
    returnedDeclination.inDegrees = (mas / MILLISECS_PER_UNIT)
  } else {
    returnedDeclination.isValid = false
    returnedDeclination.mas = 0
    returnedDeclination.inDegrees = 0
    returnedDeclination.validationError = 'Invalid number : ' + declination.formattedValue
  }
  return (returnedDeclination)
}

/**
 * Creates a new declination, with a correctly formatted
 * declination string - providing that the mas value
 * on the declination has been correctly set.
 */
export function reformatFormattedValue (declination) {
  const returnedDeclination = Object.assign({}, declination)
  var inpValue = returnedDeclination.mas
  if (inpValue.length === 0) {
    returnedDeclination.mas = 0
    returnedDeclination.inDegrees = 0
    returnedDeclination.validationError = 'Declination is required'
    returnedDeclination.isValid = false
    return returnedDeclination
  }

  const result = milliarcsecondsToString(inpValue)
  if (result === null) {
    returnedDeclination.isValid = false
  } else {
    returnedDeclination.formattedValue = result
  }
  return returnedDeclination
}

/**
 * Converts milliarcseconds to a String in the format +UU:MM:SS.ss, where +
 * is an optional sign or space, UU is hours or degrees, MM is minutes, SS
 * is seconds and sss is milliseconds
 *
 * @return input converted to a String
 */
export function milliarcsecondsToString (mas) {
  var inpValue = mas
  var result = null

  const zeros = '000000'
  // Stay at the precision of the incoming value
  if (isFinite(inpValue)) {
    var e = 1
    while ((Math.round(inpValue * e) / e) !== inpValue) {
      e *= 10
    }
  }

  var sign = ' '
  if (inpValue < 0) {
    sign = '-'
    inpValue = -inpValue
  }

  // Calculate units mins and secs
  const units = Math.trunc(inpValue / MILLISECS_PER_UNIT)
  var remainder = inpValue - (units * MILLISECS_PER_UNIT)
  const mins = Math.trunc(remainder / MILLISECS_PER_MINUTE)
  remainder -= (mins * MILLISECS_PER_MINUTE)

  const secs = (remainder / MILLISECS_PER_SEC).toFixed(2)
  const fSecs = (zeros + secs).slice(-5)

  const paddedDeg = (sign + zeros + units).slice(-2)
  const paddedMin = (zeros + mins).slice(-FW_BEFORE)

  if (isNaN(paddedDeg) || isNaN(paddedMin) || isNaN(fSecs)) {
    result = null
  } else {
    result = sign + paddedDeg + ':' + paddedMin + ':' + fSecs
  }
  return result
}

/**
 * Convert a value in velocity units into frequency
 *
 * @return velocity value as a frequency in GHz
 */
export function velocityToGHz (speedinKms, skyFrequencyInGHz) {
  const C = 2.99792458e8
  const LIGHT_SPEED_C_IN_KMS = C / 1.0E3
  const r = speedinKms / LIGHT_SPEED_C_IN_KMS
  return (skyFrequencyInGHz * r)
}
